import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { productService } from '../lib/firebaseService';
import { Upload, FileText, CheckCircle, Clock, Trash2 } from 'lucide-react';
import ImportWizard from '../components/ImportWizard';
import { t } from '../utils/translation';
import '../styles/components-extended.css';
import { format } from 'date-fns';

export default function Imports() {
    const [isWizardOpen, setIsWizardOpen] = useState(false);
    const queryClient = useQueryClient();
    const { data: imports, isLoading } = useQuery({ queryKey: ['imports'], queryFn: productService.getImports });

    const deleteMutation = useMutation({
        mutationFn: productService.deleteSalesImport,
        onSuccess: () => {
            queryClient.invalidateQueries(['imports']);
        }
    });

    const handleDelete = async (id) => {
        if (window.confirm(t('IMPORTS.DELETE_CONFIRM'))) {
            await deleteMutation.mutateAsync(id);
        }
    };

    return (
        <div className="max-w-[1200px] mx-auto">
            <div className="page-header">
                <div>
                    <h1 className="page-title">{t('IMPORTS.TITLE')}</h1>
                    <p className="page-subtitle">{t('IMPORTS.SUBTITLE')}</p>
                </div>
                <button onClick={() => setIsWizardOpen(true)} className="btn btn-primary h-10 px-6 shadow-sm hover:shadow-md">
                    <Upload size={18} className="mr-2" />
                    {t('IMPORTS.NEW_IMPORT')}
                </button>
            </div>

            <div className="content-card">
                <div className="table-container">
                    <table className="data-table">
                        <thead>
                            <tr>
                                <th>{t('IMPORTS.TABLE.REFERENCE')}</th>
                                <th className="hidden-mobile">{t('IMPORTS.TABLE.EVENT')}</th>
                                <th>{t('IMPORTS.TABLE.STATUS')}</th>
                                <th className="hidden-mobile">{t('IMPORTS.TABLE.TRANSACTIONS')}</th>
                                <th>{t('IMPORTS.TABLE.TOTAL')}</th>
                                <th>{t('IMPORTS.TABLE.ACTIONS')}</th>
                            </tr>
                        </thead>
                        <tbody>
                            {isLoading ? (
                                <tr><td colSpan="5" className="text-center py-8">{t('UI.LOADING')}</td></tr>
                            ) : imports?.length > 0 ? (
                                imports.map(imp => (
                                    <tr key={imp.id}>
                                        <td>
                                            <div className="flex items-center">
                                                <div className="p-2 bg-blue-50 text-blue-600 rounded-lg mr-3">
                                                    <FileText size={20} />
                                                </div>
                                                <div>
                                                    <div className="font-semibold text-gray-900">{imp.fileName}</div>
                                                    <div className="text-xs text-gray-500">{format(new Date(imp.date), 'MMM d, yyyy • HH:mm')}</div>
                                                </div>
                                            </div>
                                        </td>
                                        <td className="font-medium text-gray-700 hidden-mobile">{imp.eventName}</td>
                                        <td>
                                            <span className="badge badge-green flex items-center w-fit">
                                                <CheckCircle size={12} className="mr-1" /> {t('IMPORTS.STATUS.COMPLETED')}
                                            </span>
                                        </td>
                                        <td className="font-medium hidden-mobile">{imp.itemsCount} {t('IMPORTS.ITEMS')}</td>
                                        <td className="font-bold text-gray-900">€{imp.totalRevenue?.toLocaleString()}</td>
                                        <td>
                                            <button
                                                onClick={() => handleDelete(imp.id)}
                                                className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-md transition-colors"
                                                title={t('UI.DELETE_IMPORT')}
                                            >
                                                <Trash2 size={16} />
                                            </button>
                                        </td>
                                    </tr>
                                ))
                            ) : (
                                <tr>
                                    <td colSpan="5" className="text-center py-12">
                                        <div className="flex flex-col items-center justify-center text-gray-400">
                                            <Upload size={48} strokeWidth={1} className="mb-4 opacity-50" />
                                            <p className="text-lg font-medium text-gray-900 mb-1">{t('IMPORTS.EMPTY.TITLE')}</p>
                                            <p className="text-sm">{t('IMPORTS.EMPTY.DESC')}</p>
                                            <button onClick={() => setIsWizardOpen(true)} className="mt-4 btn btn-secondary">
                                                {t('IMPORTS.EMPTY.BTN')}
                                            </button>
                                        </div>
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>
            </div>

            {isWizardOpen && (
                <ImportWizard
                    isOpen={isWizardOpen}
                    onClose={() => setIsWizardOpen(false)}
                />
            )}
        </div>
    );
}
